import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, Download } from "lucide-react";
import Layout from "@/components/Layout";
import { api, formatPrice, resolveImage } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      setPollCount(attempts);
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        setStatus(data);
        if (data.payment_status === "paid") {
          if (!cleared.current) {
            cleared.current = true;
            clear();
            localStorage.removeItem("st_pending_session");
          }
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") return;
        if (attempts < 15) setTimeout(poll, 2000);
      } catch (e) {
        if (attempts < 15) setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]); // eslint-disable-line

  const paid = status?.payment_status === "paid";
  const failed = status?.payment_status === "failed" || status?.payment_status === "expired";

  return (
    <Layout>
      <div className="px-4 pt-10 pb-8 text-center max-w-md mx-auto">
        {paid ? (
          <>
            <div className="mx-auto w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-12 h-12 text-emerald-600" strokeWidth={2.5} />
            </div>
            <h1 data-testid="payment-success-title" className="mt-5 font-heading font-black text-2xl text-brand-blue">Pagamento completato!</h1>
            <p className="mt-2 text-slate-600">Grazie per il tuo ordine. Riceverai una email di conferma.</p>
            <div className="mt-5 bg-white border border-border rounded-2xl p-5 text-left">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Numero ordine</div>
              <div data-testid="order-number" className="font-heading font-black text-lg text-brand-blue">{status.order_number}</div>
              <div className="mt-3 flex justify-between text-sm">
                <span className="text-slate-500">Totale pagato</span>
                <span className="font-heading font-black text-brand-blue">{formatPrice(status.total)}</span>
              </div>
            </div>
            {status.downloads?.length > 0 && (
              <div className="mt-4 bg-white border border-border rounded-2xl p-5 text-left" data-testid="order-downloads">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">I tuoi PDF</div>
                <div className="space-y-2">
                  {status.downloads.map((d) => (
                    <a key={d.pdf_id} href={resolveImage(d.url)} data-testid={`download-${d.pdf_id}`} className="flex items-center gap-3 rounded-xl bg-brand-yellow/20 hover:bg-brand-yellow/40 px-3 py-2 text-sm font-bold text-brand-blue transition-colors">
                      <Download className="w-4 h-4" />
                      <span className="flex-1 truncate">{d.title}</span>
                      {d.kind === "gift" && <span className="text-[10px] uppercase text-emerald-700">Omaggio</span>}
                    </a>
                  ))}
                </div>
                <p className="text-[11px] text-slate-400 mt-3">Salva questa pagina: i link restano validi per il tuo ordine.</p>
              </div>
            )}
            <Link to="/" data-testid="back-home-btn" className="mt-6 inline-flex items-center justify-center bg-brand-yellow hover:bg-brand-yellow-dark text-brand-blue font-bold rounded-full px-6 py-3">
              Torna alla home
            </Link>
          </>
        ) : failed ? (
          <>
            <div className="mx-auto w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
              <XCircle className="w-12 h-12 text-red-500" strokeWidth={2.5} />
            </div>
            <h1 className="mt-5 font-heading font-black text-2xl text-brand-blue">Pagamento non completato</h1>
            <p className="mt-2 text-slate-600">Non è stato possibile completare il pagamento. Il carrello è ancora disponibile.</p>
            <Link to="/carrello" className="mt-6 inline-flex items-center justify-center bg-brand-blue text-white font-bold rounded-full px-6 py-3">
              Torna al carrello
            </Link>
          </>
        ) : (
          <>
            <div className="mx-auto w-20 h-20 rounded-full bg-brand-yellow/20 flex items-center justify-center">
              <Clock className="w-12 h-12 text-brand-blue animate-pulse" strokeWidth={2.5} />
            </div>
            <h1 className="mt-5 font-heading font-black text-xl text-brand-blue">Conferma pagamento in corso...</h1>
            <p className="mt-2 text-slate-500 text-sm">Attesa conferma da Stripe ({pollCount}/15)</p>
          </>
        )}
      </div>
    </Layout>
  );
}
